import React from 'react';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { Button, Modal, NetworkOrb, SpeechBubble, SPEECH_BUBBLE_TAIL_TOP } from '@aibuild/ui';
import { authPathWithReturnTo } from '@utils/ssoSession';
import { Welcome } from './SignupGateDialog.styled';

/** What the visitor tried to do that needs an account. */
export type SignupReason = 'run' | 'aiRender';

const ORB_PX = 96;

interface SignupGateDialogProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly appName: string;
  /** Where the visitor comes back to once they have an account. */
  readonly returnTo: string;
  readonly reason?: SignupReason;
}

const SignupGateDialog: React.FC<SignupGateDialogProps> = ({ open, onClose, appName, returnTo, reason = 'run' }) => {
  const intl = useIntl();
  const navigate = useNavigate();
  const rendering = reason === 'aiRender';
  return (
    <Modal
      open={open}
      onClose={onClose}
      size="sm"
      title={
        rendering
          ? intl.formatMessage({ id: 'apps.public.gate.render.title', defaultMessage: 'Create account' })
          : intl.formatMessage(
              { id: 'apps.public.gate.title', defaultMessage: 'Create a free account to run {app}' },
              { app: appName },
            )
      }
      footer={
        <>
          <Button variant="text" onClick={() => navigate(authPathWithReturnTo('login', returnTo))} dataTestId="public-app__gate-login">
            {intl.formatMessage({ id: 'apps.public.gate.login', defaultMessage: 'I have an account' })}
          </Button>
          <Button onClick={() => navigate(authPathWithReturnTo('signup', returnTo))} dataTestId="public-app__gate-signup">
            {intl.formatMessage({ id: 'apps.public.gate.signup', defaultMessage: 'Sign up' })}
          </Button>
        </>
      }
      dataTestId="public-app__gate"
    >
      {rendering ? (
        <Welcome>
          <NetworkOrb size={ORB_PX} interactive={false} />
          <SpeechBubble tail={SPEECH_BUBBLE_TAIL_TOP} dataTestId="public-app__gate-bubble">
            {intl.formatMessage({
              id: 'apps.public.gate.render.bubble',
              defaultMessage: 'Hi! New accounts get free AI credits.',
            })}
          </SpeechBubble>
          {intl.formatMessage({ id: 'apps.public.gate.render.body', defaultMessage: 'Get started with Aibuild OS.' })}
        </Welcome>
      ) : (
        intl.formatMessage({
          id: 'apps.public.gate.body',
          defaultMessage:
            'Running an app uses AI on our servers, so it needs an account. What you set up here is kept, and you can pick it up right after you sign in.',
        })
      )}
    </Modal>
  );
};

export default SignupGateDialog;
